import {useContext, useEffect, useMemo, type MouseEvent} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import {Button, Grid, Typography} from '@mui/material';

import {EmpionPaper} from '../../../components/EmpionPaper/EmpionPaper';
import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {LoadingOverlay} from '../../../components/Loading/LoadingOverlay';
import {CompanyContext} from '../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {ApplicantResumeDownloadAction} from '../../../features/company/recruiting/applicants/components/DetailedCardView/ApplicantResumeDownloadAction';
import {ApplicantSkills} from '../../../features/company/recruiting/applicants/components/DetailedCardView/ApplicantSkills';
import {Languages} from '../../../features/company/recruiting/applicants/components/DetailedCardView/Languages';
import {WorkExperiences} from '../../../features/company/recruiting/applicants/components/DetailedCardView/WorkExperiences';
import {HeaderInformation} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/HeaderInformation';
import {MatchFit} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/MatchFit/MatchFit';
import {
  applicantFromTalentPoolProfileResponse,
  applicationsFromTalentPoolProfileResponse,
  matchDataStubForTalentPoolContact,
} from '../../../features/company/recruiting/talent-pool/serializers/TalentPoolProfileSerializers';
import {TalentPoolApplicationCard} from '../../../features/company/recruiting/talent-pool/TalentPoolApplicationCard';
import {
  TalentPoolProfileApplication,
  TalentPoolProfileResponse,
} from '../../../features/company/recruiting/talent-pool/types/TalentPoolProfileTypes';
import {useApiRead} from '../../../hooks/useApiRead';
import {greenColors} from '../../../layouts/theme/designSystem';
import {GetRoutes} from '../../../lib/api/GET/GetRoutes';
import {AppConfig} from '../../../lib/AppConfig';
import {
  MATCH_DETAILS_FROM_MATCHES,
  MATCH_DETAILS_FROM_TALENT_POOL,
  MATCH_DETAILS_RETURN_TO_PARAM,
  type MatchDetailsLocationState,
} from '../../../lib/matchDetailsNav';
import {getApplicantResumeUrl} from '../../../types/shared-recruiting-types';

export const TALENT_POOL_PROFILE_QUERY_KEY = 'company-talent-pool-profile';

function hasWaitingCvPoolApplication(applications: TalentPoolProfileApplication[]) {
  return applications.some((app) => app.poolStatus === 'waiting_cv');
}

export const TalentPoolProfilePage = () => {
  const urlParams = useParams();
  const talentId = urlParams.id || '';
  const navigate = useNavigate();
  const location = useLocation();
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useContext(LanguageContext);
  const {t} = useTranslation();

  const {
    data,
    error: profileError,
    isLoading,
  } = useApiRead<TalentPoolProfileResponse>({
    path: GetRoutes.companyTalentPoolProfile(companyId, talentId),
    options: {
      enabled: !!talentId && !!companyId,
      queryKey: [TALENT_POOL_PROFILE_QUERY_KEY, companyId, talentId],
    },
  });

  const applicant = useMemo(() => applicantFromTalentPoolProfileResponse(data), [data]);
  const applications = useMemo(() => applicationsFromTalentPoolProfileResponse(data), [data]);
  const matchedApplications = useMemo(
    () => applications.filter((app) => Boolean(app.match?.resourceId)),
    [applications]
  );
  const poolApplications = useMemo(() => applications.filter((app) => !app.match?.resourceId), [applications]);
  const displayMatch = useMemo(() => {
    if (!applicant) {
      return undefined;
    }
    return matchDataStubForTalentPoolContact(applicant);
  }, [applicant]);

  useEffect(() => {
    const urlLang = searchParams.get('lang');

    if (urlLang !== lang) {
      setSearchParams(
        (prev) => {
          prev.set('lang', lang);
          return prev;
        },
        {replace: true, state: location.state}
      );
    }
  }, [lang, searchParams, setSearchParams, location.state]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (profileError) {
    return <ErrorMessage />;
  }

  if (!applicant || !displayMatch) {
    return null;
  }

  const resumeUrl = getApplicantResumeUrl(applicant);
  const showCvDownloadDisabled = !resumeUrl && hasWaitingCvPoolApplication(applications);

  const navState = location.state as MatchDetailsLocationState | null;
  const returnTo = searchParams.get(MATCH_DETAILS_RETURN_TO_PARAM);

  const returnPath =
    returnTo === MATCH_DETAILS_FROM_TALENT_POOL
      ? AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PATH
      : returnTo === MATCH_DETAILS_FROM_MATCHES
        ? AppConfig.COMPANY_ADMIN_RECRUITING_MATCHES_PATH
        : navState?.matchDetailsReturnPath ?? AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PATH;

  const backLabel =
    returnPath === AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PATH
      ? t('appHeader.menuLabels.companyAdmin.recruiting.talentPool')
      : t('appHeader.menuLabels.companyAdmin.recruiting.matches');

  const onBack = (e: MouseEvent) => {
    e.stopPropagation();
    if (returnTo === MATCH_DETAILS_FROM_TALENT_POOL || returnTo === MATCH_DETAILS_FROM_MATCHES) {
      const q = new URLSearchParams(searchParams);
      q.delete(MATCH_DETAILS_RETURN_TO_PARAM);
      const s = q.toString();
      navigate(s ? `${returnPath}?${s}` : returnPath);
      return;
    }
    const returnSearch = navState?.matchDetailsReturnSearch;
    navigate(returnSearch ? `${returnPath}${returnSearch}` : returnPath);
  };

  return (
    <EmpionPaper
      id='talent-pool-profile-view'
      sx={{
        padding: 4,
        borderRadius: '16px',
      }}
    >
      <Button
        onClick={onBack}
        style={{display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '24px', color: greenColors[900]}}
      >
        <ArrowBackOutlinedIcon />
        <Typography variant='body1'>{backLabel}</Typography>
      </Button>
      <Grid container direction='column'>
        <HeaderInformation match={displayMatch} variant='talentPool' />
        <ApplicantResumeDownloadAction resumeUrl={resumeUrl} showDisabledWhenNoResume={showCvDownloadDisabled} />
        <MatchFit match={displayMatch} marginTop={3} showScoreCards={false} />
        <Grid container mb={2} marginTop={2}>
          <ApplicantSkills applicantSkills={applicant.skills} />
        </Grid>
        <Grid container mb={2}>
          <WorkExperiences workExperiences={applicant.workExperiences} />
        </Grid>
        <Grid container mb={2}>
          <Languages languages={applicant?.languages} />
        </Grid>
        {poolApplications.length > 0 ? (
          <Grid container direction='column' mt={1}>
            <Typography variant='h2' mb={2}>
              {t('talentPoolProfilePage.applicationsTitle')}
            </Typography>
            {poolApplications.map((app) => (
              <TalentPoolApplicationCard key={app.resourceId} application={app} />
            ))}
          </Grid>
        ) : null}
        {matchedApplications.length > 0 ? (
          <Grid container direction='column' mt={1}>
            <Typography variant='h2' mb={2}>
              {t('talentPoolProfilePage.matchesTitle')}
            </Typography>
            {matchedApplications.map((app) => (
              <TalentPoolApplicationCard key={app.resourceId} application={app} />
            ))}
          </Grid>
        ) : null}
      </Grid>
    </EmpionPaper>
  );
};
